import { z } from "zod";

/**
 * Schemas for the Bedrock registry — the persistent registry of
 * project-authored items (templates, skills, etc.) served by
 * `apps/bedrock-api` and backed by Postgres + R2.
 *
 * Per the "PG = searchable + listing display only" rule:
 * - Top-level row columns hold identity/search/preview fields.
 * - `metadata` JSONB holds ONLY per-type fields the listing UI displays that
 *   aren't covered by standard columns. Types with no metadata store `null`.
 * - Detailed content (full file trees, npm dependencies) lives in the R2
 *   `.registry.json` doc, not the PG row.
 * - Preview imagery is referenced by external URL via `previewUrls`; the
 *   registry does not host preview blobs.
 */

export const registryItemTypeSchema = z
  .enum([
    "site",
    "page",
    "section",
    "skill",
    "plan",
    "branding",
    "product",
    "reference",
  ])
  .describe("RegistryItemType");

// Write-boundary contract: every new item is either publicly listed or
// reachable only by direct link. Row/read schemas still type `visibility` as
// a plain string until production rows are verified to only contain these
// values.
export const registryItemVisibilitySchema = z
  .enum(["public", "unlisted"])
  .describe("RegistryItemVisibility");

// Slugs become R2 keys and public URLs, so the format is restricted to
// lowercase kebab/snake segments.
export const registrySlugSchema = z
  .string()
  .min(1)
  .max(120)
  .regex(/^[\da-z][\d_a-z-]*$/);

export const registryItemIdSchema = z.uuid();

export const REGISTRY_FILE_PATH_REQUIREMENT_MESSAGE =
  "Registry file paths must be canonical relative POSIX paths";

export const REGISTRY_APP_ROOT_REQUIREMENT_MESSAGE =
  "Registry paths must be canonical relative POSIX paths; page and site registry items must use root app/ and must not include src/app/";

function isCanonicalRegistryFilePath(filePath: string) {
  const segments = filePath.split("/");
  return (
    !filePath.includes("\\") &&
    segments.every((segment) => {
      return (
        segment.length > 0 &&
        segment === segment.trim() &&
        segment !== "." &&
        segment !== ".."
      );
    })
  );
}

const registryFilePathSchema = z
  .string()
  .min(1)
  .refine(isCanonicalRegistryFilePath, {
    message: REGISTRY_FILE_PATH_REQUIREMENT_MESSAGE,
  });

export const registryContentFilesSchema = z
  .record(registryFilePathSchema, z.string())
  .refine((files) => Object.keys(files).length > 0, {
    message: "Registry item content must include at least one file",
  });

export function hasRequiredRegistryAppRoot({
  files,
  type,
}: {
  files: Record<string, string>;
  type: z.infer<typeof registryItemTypeSchema>;
}) {
  const filePaths = Object.keys(files);
  if (filePaths.length === 0 || !filePaths.every(isCanonicalRegistryFilePath)) {
    return false;
  }
  if (type !== "page" && type !== "site") {
    return true;
  }
  return (
    filePaths.some((filePath) => filePath.startsWith("app/")) &&
    filePaths.every((filePath) => {
      return filePath !== "src/app" && !filePath.startsWith("src/app/");
    })
  );
}

function validateRegistryAppRoot(
  item: {
    files: Record<string, string>;
    type: z.infer<typeof registryItemTypeSchema>;
  },
  context: z.RefinementCtx,
) {
  if (!hasRequiredRegistryAppRoot(item)) {
    context.addIssue({
      code: "custom",
      message: REGISTRY_APP_ROOT_REQUIREMENT_MESSAGE,
      path: ["files"],
    });
  }
}

export const registryAppRootRequirementSchema = z
  .object({
    files: registryContentFilesSchema,
    type: registryItemTypeSchema,
  })
  .superRefine(validateRegistryAppRoot);

export const registryItemSourceIdentitySchema = z
  .object({
    sourceType: z.string().min(1).max(80),
    sourceKey: z.string().min(1).max(512),
  })
  .describe("RegistryItemSourceIdentity");

// Display-only fields for visual templates (sites, pages, sections, ads).
// These are not searchable or filterable on the row — they're rendered on
// preview cards in the agent-ui registry browser when present.
//
// `brandDomain` is consumed client-side by `resolveBrandDisplay` to derive
// a Clearbit logo URL when no curated `logoUrl` is uploaded yet, so that
// new items can light up the brand row without a separate asset upload.
const visualDisplayMetadataSchema = z
  .object({
    brandName: z.string().optional(),
    brandDomain: z.string().optional(),
    logoUrl: z.url().optional(),
  })
  .strict();

// A written design description paired with reference screenshots — the unit
// the image-to-design pipeline sends to the model verbatim (description +
// screenshots together; either alone regresses to generic output).
export const registryDesignDescriptionSchema = z
  .object({
    description: z.string().min(1),
    aestheticFamily: z.string().min(1),
    pageType: z.string().min(1),
    screenshotUrls: z.array(z.url()).min(1).max(4),
  })
  .strict()
  .describe("RegistryDesignDescription");

// One previewable page of a site template — a live iframe-able URL on the static gallery; `route` is the in-site path (`/`, `/pricing`) and `name` is the human label shown in the preview carousel.
const siteTemplatePageSchema = z
  .object({
    route: z.string(),
    name: z.string(),
    url: z.url(),
    design: registryDesignDescriptionSchema.optional(),
  })
  .strict();

// Site templates extend the shared visual fields with two pointers: `artifactRepo` (the Cloudflare Artifacts repo to FORK on "Create Site" — the clone source, not the R2 `files` tree) and `liveUrl` + `pages` (live iframe-able URLs for the preview carousel).
export const siteRegistryItemMetadataSchema = visualDisplayMetadataSchema
  .extend({
    artifactRepo: z.string().optional(),
    liveUrl: z.url().optional(),
    pages: z.array(siteTemplatePageSchema).optional(),
  })
  .describe("SiteRegistryItemMetadata");

export const pageRegistryItemMetadataSchema = visualDisplayMetadataSchema
  .extend({
    keyFeatures: z.array(z.string().min(1).max(80)).max(6).optional(),
    sectionsIncluded: z.array(z.string().min(1)).max(12).optional(),
    design: registryDesignDescriptionSchema.optional(),
  })
  .describe("PageRegistryItemMetadata");

export const sectionRegistryItemMetadataSchema = visualDisplayMetadataSchema
  .extend({
    design: registryDesignDescriptionSchema.optional(),
  })
  .describe("SectionRegistryItemMetadata");

export const brandingRegistryItemMetadataSchema = z
  .object({
    palette: z.array(z.string()).max(8).optional(),
    logoUrl: z.url().optional(),
    faviconUrl: z.url().optional(),
    tagline: z.string().max(140).optional(),
  })
  .strict()
  .describe("BrandingRegistryItemMetadata");

export const skillRegistryItemMetadataSchema = z
  .object({
    command: z.string().optional(),
    tagline: z.string().optional(),
    // (Ezra, 2026-07-09, REPL-29107): marks a catalog skill that bedrock-api
    // preinstalls into every user's workspace skills. String-valued because
    // catalog metadata is a string record end to end.
    preinstall: z.literal("true").optional(),
  })
  .strict()
  .describe("SkillRegistryItemMetadata");

// Listing/search metadata for provider-backed commerce products (e.g. Printful
// blank catalog items). Identifies the upstream provider product and lifts
// provider-neutral facets for search: decoration methods and one facet per
// option axis (Color/Size for garments, Flavor/Count for supplements). The
// full normalized CatalogProduct document lives in the R2 content blob, not
// the PG row.
export const productRegistryItemMetadataSchema = z
  .object({
    provider: z.string(),
    providerDisplayName: z.string(),
    providerProductId: z.string(),
    type: z.string(),
    categoryIds: z.array(z.string()).default([]),
    defaultVariantId: z.string().nullish(),
    areaCount: z.number().default(0),
    methods: z.array(z.string()).default([]),
    optionFacets: z
      .array(
        z.object({ name: z.string(), values: z.array(z.string()) }).strict(),
      )
      .default([]),
  })
  .strict()
  .describe("ProductRegistryItemMetadata");

// Per-type `metadata` validation, keyed so callers can enforce the schema a
// specific item type requires (`plan` items carry no metadata). Used by the
// bedrock-api catalog upsert boundary and the registry-templates sync so both
// sides reject the same payloads.
// The two shapes of `reference` metadata, discriminated on `kind`.
// Conversion guidance shipped as catalog rows (Sidd's CRO corpus): faceted by
// scope so agents load exactly the slice that matches the build.
export const croGuidanceMetadataSchema = z
  .object({
    kind: z.literal("cro"),
    scope: z.enum(["universal", "offer", "vertical", "page-type"]),
    vertical: z.string().optional(),
    pageType: z.string().optional(),
  })
  .strict()
  .describe("CroGuidanceMetadata");

// Code-optional design references: a screenshot + written reference, with an
// optional pointer to the buildable site item when code exists.
export const designReferenceMetadataSchema = z
  .object({
    kind: z.literal("design"),
    aestheticFamily: z.string(),
    pageType: z.string(),
    sourceUrl: z.string().optional(),
    linkedSiteSlug: z.string().optional(),
  })
  .strict()
  .describe("DesignReferenceMetadata");

// A `reference` item is either CRO guidance or a design reference; `category`
// ("cro" | "design-inspiration") is the search-time discriminator.
export const referenceRegistryItemMetadataSchema = z
  .discriminatedUnion("kind", [
    croGuidanceMetadataSchema,
    designReferenceMetadataSchema,
  ])
  .describe("ReferenceRegistryItemMetadata");

export const registryItemMetadataSchemaByType: Record<
  z.infer<typeof registryItemTypeSchema>,
  z.ZodType | null
> = {
  site: siteRegistryItemMetadataSchema,
  page: pageRegistryItemMetadataSchema,
  section: sectionRegistryItemMetadataSchema,
  skill: skillRegistryItemMetadataSchema,
  branding: brandingRegistryItemMetadataSchema,
  product: productRegistryItemMetadataSchema,
  plan: null,
  reference: referenceRegistryItemMetadataSchema,
};

export const genericRegistryItemMetadataSchema = z
  .union([
    siteRegistryItemMetadataSchema,
    pageRegistryItemMetadataSchema,
    sectionRegistryItemMetadataSchema,
    skillRegistryItemMetadataSchema,
    brandingRegistryItemMetadataSchema,
    productRegistryItemMetadataSchema,
    croGuidanceMetadataSchema,
    designReferenceMetadataSchema,
  ])
  .describe("GenericRegistryItemMetadata");

// NOTE (Cole, 2026-05-01): I want to rename this to RegistryItem, but that type is already taken.
// I'll combine this on a future pr.
//
// `previewHtml` is intentionally NOT on the listing/metadata read shape — it's
// a heavy rendered HTML blob (tens to hundreds of KB per row). Listing
// responses ship many rows and can't afford that bloat. Callers that need
// `previewHtml` for a single item should call the DB-backed single-item read
// (`registry.get({ id })` / `GET /items/:id`), which returns the
// `RegistryItemDetail` shape below.
export const registryItemMetadataSchema = z
  .object({
    id: z.string(),
    slug: z.string(),
    name: z.string(),
    type: registryItemTypeSchema,
    metadata: genericRegistryItemMetadataSchema.nullable(),
    projectId: z.string().nullable(),
    createdBy: z.string().nullable(),
    sourceType: z.string().nullable(),
    sourceKey: z.string().nullable(),
    shortLink: z.string().nullable(),
    description: z.string().nullable(),
    visibility: z.string(),
    tags: z.array(z.string()),
    category: z.string().nullable(),
    previewUrls: z.array(z.string()),
    createdAt: z.string(),
    updatedAt: z.string(),
  })
  .describe("RegistryItemMetadata");

export const registryListItemsResultSchema = z
  .object({
    items: z.array(registryItemMetadataSchema),
    nextCursor: z.string().nullable(),
  })
  .describe("FindRegistryItemsResult");

export const registryItemCategoriesResultSchema = z
  .object({
    categories: z.array(z.string()),
  })
  .describe("RegistryItemCategoriesResult");

export const registryItemTagsResultSchema = z
  .object({
    tags: z.array(z.string()),
  })
  .describe("RegistryItemTagsResult");

// Single-item DB read shape: listing metadata plus the rendered `previewHtml`
// for preview surfaces (template cards, modals, admin row detail). The R2
// document is not consulted on this path; `previewHtml` lives on the
// Postgres row.
export const registryItemDetailSchema = registryItemMetadataSchema
  .extend({
    previewHtml: z.string().nullable(),
  })
  .describe("RegistryItemDetail");

// Public, unauthenticated preview read. Identity attribution (`projectId` /
// `createdBy`) is always stripped to null server-side before returning, so
// the schema pins both to `z.null()` to enforce the invariant at the
// response boundary.
export const registryItemPreviewResultSchema = z
  .object({
    item: registryItemDetailSchema.extend({
      projectId: z.null(),
      createdBy: z.null(),
    }),
  })
  .describe("RegistryItemPreviewResult");

// R2-backed content for a single registry item. Returned by the explicit
// content endpoint and used by install / edit / download flows that need the
// file tree and npm dependencies. `previewHtml` is intentionally NOT on this
// shape — it's a DB column, served by `registry.get({ id })` instead.
export const registryItemContentSchema = z
  .object({
    meta: z.object({
      id: z.string(),
      slug: z.string(),
      type: registryItemTypeSchema,
      name: z.string(),
      description: z.string().nullable(),
    }),
    files: z.record(z.string(), z.string()),
    dependencies: z.array(z.string()),
  })
  .describe("RegistryItemContent");

// The full self-describing registry document: the `.registry.json` shape
// stored in R2 and the body accepted by the admin document upsert/import
// routes. This is the durable source-of-truth shape; PG rows are a projection
// of it.
export const registryItemDocumentSchema = z
  .object({
    id: z.uuid(),
    slug: z.string().min(1),
    name: z.string().min(1),
    type: registryItemTypeSchema,
    projectId: z.string().min(1).nullable(),
    createdBy: z.string().min(1).nullable(),
    description: z.string().nullable(),
    visibility: z.string(),
    tags: z.array(z.string()),
    category: z.string().nullable(),
    sourceType: z.string().nullable().default(null),
    sourceKey: z.string().nullable().default(null),
    shortLink: z.string().nullable().default(null),
    previewUrls: z.array(z.string()),
    previewHtml: z.string().nullable(),
    metadata: z.record(z.string(), z.unknown()).nullable(),
    files: registryContentFilesSchema,
    dependencies: z.array(z.string()),
    embeddingText: z.string(),
    embedding: z.array(z.number()),
    createdAt: z.string().min(1),
    updatedAt: z.string().min(1),
  })
  .describe("RegistryItemDocument");

// Create/upsert input shared by `POST /api/v1/registry/items` and the MCP
// `create_registry_item` tool. Ownership (`projectId` / `createdBy`) is
// always overridden server-side from the authenticated request context;
// the fields exist here so source artifacts can round-trip through the
// same shape.
export const registryCreateItemInputSchema = z
  .object({
    item: z
      .object({
        id: registryItemIdSchema,
        slug: registrySlugSchema,
        name: z.string().min(1),
        type: registryItemTypeSchema,
        projectId: z.string().min(1).nullable().default(null),
        createdBy: z.string().min(1).nullable().default(null),
        sourceType: z.string().min(1).max(80).nullable().default(null),
        sourceKey: z.string().min(1).max(512).nullable().default(null),
        description: z.string().nullable().default(null),
        metadata: z.record(z.string(), z.unknown()).nullable().default(null),
        // (Ezra, 2026-07-07, REPL-27571): owned channels (REST create, MCP
        // create_registry_item) may not author public items — only the internal
        // registry-catalog endpoint does — so an omitted visibility must resolve
        // to the project-scoped lane instead of silently going public.
        visibility: registryItemVisibilitySchema.default("unlisted"),
        tags: z.array(z.string()).default([]),
        category: z.string().nullable().default(null),
        previewUrls: z.array(z.string()).default([]),
        previewHtml: z.string().nullable().default(null),
        files: registryContentFilesSchema,
        dependencies: z.array(z.string()).default([]),
      })
      .superRefine(validateRegistryAppRoot),
  })
  .describe("RegistryCreateItemInput");

export const registryCreateItemOutputSchema = z
  .object({
    created: z.object({
      id: registryItemIdSchema,
      type: registryItemTypeSchema,
      slug: registrySlugSchema,
      sourceType: z.string().nullable(),
      sourceKey: z.string().nullable(),
      url: z
        .url()
        .describe(
          "Canonical public-facing URL for the new registry item. The skill returns this to the user immediately so they can share / preview without waiting on verification.",
        ),
      shareUrl: z.url().nullable(),
    }),
  })
  .describe("RegistryCreateItemOutput");

// Authoring body for the internal registry-catalog endpoint (`PUT .../catalog/:type/:slug`): type-generic across registry item types, server-controlled identity omitted, global-catalog items written with null ownership, and PUT replacement semantics (omitted optional fields reset to defaults).
export const registryCatalogUpsertInputSchema = z
  .object({
    name: z.string().min(1),
    description: z.string().nullish(),
    metadata: z.record(z.string(), z.unknown()).nullish(),
    visibility: registryItemVisibilitySchema.optional(),
    tags: z.array(z.string()).optional(),
    category: z.string().nullish(),
    previewUrls: z.array(z.string()).optional(),
    previewHtml: z.string().nullish(),
    files: registryContentFilesSchema,
    dependencies: z.array(z.string()).optional(),
  })
  .describe("RegistryCatalogUpsertInput");

export type RegistryCatalogUpsertInput = z.infer<
  typeof registryCatalogUpsertInputSchema
>;
